import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';

import SystemDataService from '../../Services/SystemDataService.js';
import NewsletterService from '../../Services/NewsletterService.js';
import { getPromotionData } from '../../Public/promotion-data.js';

import Layout from '../../Utils/Navbar/navbar-layout-component.js';
import Footer from '../../Utils/Footer/footer-component.js';
import Image from '../../Utils/Image/image-component.js';
import NavLink from '../../Utils/NavLink/navlink-component.js';

export default function PromotionScreen() {
    const systemData = useSelector(s => s.systemData || {});
    const { dir = 'ltr', language = 'en', links = [] } = systemData;

    const txt = useMemo(() => getPromotionData(language), [language]);

    const logoWhite = SystemDataService.byLabel(links, 'Logo White');

    const [carouselImages, setCarouselImages] = useState([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('idle'); // idle | loading | success | error
    const [couponCode, setCouponCode] = useState('');
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        SystemDataService.getCarouselImages()
            .then(images => { if (images?.length) setCarouselImages(images.map(img => img.image)); })
            .catch(() => { });
    }, []);

    useEffect(() => {
        if (!carouselImages.length) return;
        const interval = setInterval(() => {
            setCurrentImageIndex(prev => (prev + 1) % carouselImages.length);
        }, 4000);
        return () => clearInterval(interval);
    }, [carouselImages.length]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || status === 'loading') return;
        setStatus('loading');
        try {
            const result = await NewsletterService.subscribe(email);
            setCouponCode(result?.couponCode || '');
            setStatus('success');
            setEmail('');
        } catch {
            setStatus('error');
        }
    };

    const handleCopy = () => {
        if (!couponCode) return;
        navigator.clipboard.writeText(couponCode).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Layout seo={txt.seo}>
            <div dir={dir} className="min-h-dvh flex flex-col bg-primary-950">

                {/* ── Hero ── */}
                <div className="relative flex-1 flex items-center justify-center overflow-hidden">

                    {/* Carousel background */}
                    <div className="absolute inset-0 pointer-events-none">
                        {carouselImages.length > 0 ? (
                            carouselImages.map((img, index) => {
                                const isCurrent = index === currentImageIndex;
                                return (
                                    <div
                                        key={index}
                                        className={`absolute inset-0 w-full h-full transition-all duration-1000 ease-in-out transform ${isCurrent ? 'opacity-100 scale-100 z-10' : 'opacity-0 scale-105 z-0'}`}
                                    >
                                        <Image
                                            src={img}
                                            quality={90}
                                            alt={`Slide ${index}`}
                                            className="w-full h-full object-cover object-center opacity-20 grayscale-[20%]"
                                            wrapperClassName="w-full h-full"
                                        />
                                    </div>
                                );
                            })
                        ) : (
                            txt.backgroundImage && (
                                <Image
                                    src={txt.backgroundImage}
                                    alt=""
                                    className="w-full h-full object-cover opacity-20"
                                    wrapperClassName="w-full h-full"
                                />
                            )
                        )}
                        <div className="absolute inset-0 z-20 bg-gradient-to-b from-primary-950/80 via-primary-950/30 to-primary-950" />
                    </div>

                    <div className="relative z-30 w-full max-w-[680px] mx-auto px-6 py-20 flex flex-col items-center text-center gap-8">

                        {/* Logo */}
                        {logoWhite?.image && (
                            <img src={logoWhite.image} alt="Tropical Marine Construction" className="h-10 w-auto object-contain opacity-90" />
                        )}
                        
                        {/* Badge */}
                        <div className="flex flex-col gap-1">
                            <span className="text-secondary-500 text-[10px] font-black uppercase  mx-auto lg:mx-0 w-fit border-b border-secondary-500/40 pb-1">
                                {txt.badge}
                            </span>
                        </div>
                        
                        {/* Discount + headline */}
                        <div className="flex flex-col gap-2">
                            <p className="text-secondary-500 text-7xl md:text-9xl font-black leading-none" style={{ fontFamily: 'Impact, sans-serif' }}>
                                {txt.discount}
                            </p>
                            <h1 className="text-4xl md:text-5xl font-black text-white uppercase er leading-tight text-balance" style={{ fontFamily: 'Impact, sans-serif' }}>
                                {txt.headline}
                            </h1>
                        </div>

                        {/* Subheadline */}
                        <p className="text-neutral-400 text-base md:text-lg leading-relaxed text-pretty max-w-md">
                            {txt.subheadline}
                        </p>

                        {/* Form / Success state */}
                        {status === 'success' ? (
                            <div className="w-full flex flex-col items-center gap-5">
                                <div className="flex flex-col items-center gap-2">
                                    <p className="text-white text-2xl font-black uppercase ">{txt.successHeadline}</p>
                                    <p className="text-neutral-400 text-sm text-pretty">{txt.successSubtitle}</p>
                                </div>

                                {couponCode ? (
                                    <button
                                        onClick={handleCopy}
                                        className="group flex items-center gap-4 bg-white/5 border border-white/20 hover:border-secondary-500 transition-colors rounded-xl px-8 py-5 cursor-pointer"
                                        aria-label={txt.copyLabel}
                                    >
                                        <span className="text-3xl font-black text-secondary-500 tracking-widest font-mono">
                                            {couponCode}
                                        </span>
                                        <span className="text-xs font-black text-white/50 uppercase tracking-widest group-hover:text-secondary-500 transition-colors">
                                            {copied ? txt.couponCopied : txt.copyLabel}
                                        </span>
                                    </button>
                                ) : (
                                    <p className="text-neutral-400 text-sm text-pretty">{txt.couponEmailNote}</p>
                                )}

                                <p className="text-white/30 text-xs text-pretty max-w-xs">{txt.disclaimer}</p>
                            </div>
                        ) : (
                            <div className="w-full flex flex-col gap-3">
                                <form onSubmit={handleSubmit} className="w-full flex flex-col sm:flex-row gap-3">
                                    <input
                                        type="email"
                                        required
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        placeholder={txt.emailPlaceholder}
                                        disabled={status === 'loading'}
                                        className="flex-1 bg-white/10 border border-white/20 focus:border-secondary-500 focus:outline-none text-white placeholder-white/40 rounded-xl px-5 py-3.5 text-sm font-medium transition-colors"
                                    />
                                    <button
                                        type="submit"
                                        disabled={status === 'loading'}
                                        className="bg-secondary-500 hover:bg-secondary-600 active:bg-secondary-700 text-white font-black text-sm uppercase tracking-widest px-7 py-3.5 rounded-xl transition-colors whitespace-nowrap disabled:opacity-60"
                                    >
                                        {status === 'loading' ? txt.submittingLabel : txt.submitLabel}
                                    </button>
                                </form>

                                {/* Privacy note */}
                                <p className="text-white/25 text-xs text-center leading-relaxed">
                                    {txt.privacyNote}{' '}
                                    <NavLink href="/privacy" className="text-white/40 underline underline-offset-2 hover:text-white/60 transition-colors">
                                        {txt.privacyLink}
                                    </NavLink>.
                                </p>
                            </div>
                        )}

                        {status === 'error' && (
                            <p className="text-red-400 text-sm -mt-4">Something went wrong. Please try again.</p>
                        )}

                        {/* Trust points */}
                        <div className="w-full grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 rounded-2xl overflow-hidden mt-4">
                            {txt.points.map((p, i) => (
                                <div key={i} className="bg-primary-950 px-5 py-4 flex flex-col gap-1">
                                    <span className="text-white font-black text-sm uppercase tracking-wide">{p.title}</span>
                                    <span className="text-white/40 text-xs font-normal">{p.detail}</span>
                                </div>
                            ))}
                        </div>

                    </div>
                </div>

            </div>
            <Footer />
        </Layout>
    );
}
